import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { capitalizeFirstLetter } from '../../../utils/utils';
import { CONSTANTS } from '../../../constants/constants';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import NRMisconfigurationChecksStyles from './NRMisconfigurationChecks.styles';
import Box from '@mui/system/Box';
import { useChartA11y } from '../../../hooks/useChartA11y';
const useStyles = makeStyles(NRMisconfigurationChecksStyles);

function NRMisconfigurationChecks({
  chartContainerID = '',
  labelsTitle,
  failed = 0,
  passed = 0,
  notApplicable = 0,
  dataPointSelection,
  /** When true, disables Apex animations (e.g. for react-to-print). */
  disableAnimation = false
}) {
  const theme = useTheme();
  const classes = useStyles();

  const series = useMemo(() => [failed, passed, notApplicable], [failed, passed, notApplicable]);
  const chartLabels = useMemo(
    () => [
      capitalizeFirstLetter(CONSTANTS.MISCONFIGURATION_CHECKS.LABELS.FAILED),
      capitalizeFirstLetter(CONSTANTS.MISCONFIGURATION_CHECKS.LABELS.PASSED),
      capitalizeFirstLetter(CONSTANTS.MISCONFIGURATION_CHECKS.LABELS.NOT_APPLICABLE)
    ],
    []
  );

  const options = useMemo(() => {
    const totalVulns = failed + passed + notApplicable;
    return {
      chart: {
        type: 'donut',
        animations: disableAnimation
          ? {
              enabled: false,
              speed: 0,
              animateGradually: { enabled: false, delay: 0 },
              dynamicAnimation: { enabled: false, speed: 0 }
            }
          : {
              enabled: true
            },
        events: {
          ...{ dataPointSelection: dataPointSelection },
          dataPointMouseEnter: event => {
            if (event && !!event.currentTarget) event.currentTarget.style.cursor = 'pointer';
          }
        }
      },
      colors: [theme.palette.statusPalette[1], theme.palette.statusPalette[0], theme.palette.statusPalette[2]],
      dataLabels: {
        enabled: false
      },
      fill: {
        type: 'solid'
      },
      labels: chartLabels,
      legend: {
        ...theme.typography.body2,
        labels: {
          colors: theme.palette.text.primary
        },
        show: true,
        formatter: function (val, opts) {
          return val + ' - ' + opts.w.globals.series[opts.seriesIndex];
        },
        horizontalAlign: 'left',
        markers: {
          offsetX: -5,
          width: 15,
          height: 15
        },
        itemMargin: {
          horizontal: 5,
          vertical: 3
        },
        offsetY: 60
      },
      plotOptions: {
        pie: {
          startAngle: 0,
          endAngle: 360,
          expandOnClick: false,
          donut: {
            labels: {
              show: true,
              name: {
                show: true,
                offsetY: 15,
                color: theme.palette.text.secondary,
                formatter: function () {
                  return CONSTANTS.MISCONFIGURATION_CHECKS.LABELS.TOTAL;
                }
              },
              value: {
                show: true,
                ...theme.typography.h2,
                color: theme.palette.text.primary,
                offsetY: -25,
                formatter: function () {
                  return totalVulns;
                }
              },
              total: {
                show: true,
                showAlways: true,
                label: CONSTANTS.MISCONFIGURATION_CHECKS.LABELS.TOTAL,
                ...theme.typography.subtitle2,
                color: theme.palette.text.secondary,
                formatter: function () {
                  return totalVulns;
                }
              }
            }
          }
        }
      },
      states: {
        hover: {
          filter: {
            type: 'none'
          }
        }
      },
      stroke: {
        colors: [theme.palette.background.paper]
      },
      title: {
        text: CONSTANTS.MISCONFIGURATION_CHECKS.DONUT_CHART.CHART_TITLE,
        align: 'left',
        marginBottom: 10,
        offsetX: 236,
        offsetY: 50,
        floating: true,
        style: {
          ...theme.typography.label,
          color: theme.palette.text.secondary
        }
      },
      tooltip: {
        enabled: true,
        fillSeriesColor: false,
        onDatasetHover: {
          highlightDataSeries: false
        }
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }
      ]
    };
  }, [failed, passed, notApplicable, theme, chartLabels, dataPointSelection, disableAnimation]);

  useChartA11y({
    chartContainerID,
    title: 'Misconfiguration Report Donut',
    labels: chartLabels,
    series: [{ name: labelsTitle, data: series }]
  });

  return (
    <Box
      id={chartContainerID}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        margin: '0 auto'
      }}
    >
      <ReactApexChart id={'misconfig-chart'} options={options} series={series} type="donut" width="440" height="400" className={classes.mainChart} />
    </Box>
  );
}

export default React.memo(NRMisconfigurationChecks);
